import React, { useState, useEffect, useMemo, useRef } from "react";
import { CSVLink } from "react-csv";
import API from "../../services/api";
import TableLoader from "../../shared/TableLoader";
import NoDataFound from "../../shared/NoDataFound";
import MultiSelectFilter from "../../shared/MultiSelectFilter";
import { formatSpentValue } from "../../utils/constants";

const getMonthKey = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const formatMonthHeader = (yearMonth) => {
  const [y, m] = yearMonth.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  const month = d.toLocaleString("default", { month: "short" });
  const year = String(y).slice(-2);
  return `${month}'${year}`;
};

function CategoryWiseReport({ dateRange, initiateDownloadCSV, setInitiateDownloadCSV, selectedSalesPersonId }) {
  const [categoryData, setCategoryData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const csvLinkRef = useRef(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const fetchData = async () => {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      setCategoryData([]);
      return;
    }
    try {
      setLoader(true);
      const response = await API({
        url: "/alpha/get-category-month-wise-reports",
        method: "POST",
        responseType: "json",
        data: { dateRange, sales_person_id: +selectedSalesPersonId || undefined },
      });
      if (response?.code === 200) {
        setCategoryData(Array.isArray(response.data) ? response.data : []);
      } else {
        setCategoryData([]);
      }
    } catch (error) {
      console.error("Error fetching category data", error);
      setCategoryData([]);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, selectedSalesPersonId]);

  const monthColumns = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return [];
    const start = new Date(dateRange[0]);
    const end = new Date(dateRange[1]);
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    end.setDate(1);
    end.setHours(0, 0, 0, 0);
    const months = [];
    for (let d = new Date(start); d <= end; d.setMonth(d.getMonth() + 1)) {
      months.push(getMonthKey(d));
    }
    return months;
  }, [dateRange]);

  const categoryRows = useMemo(() => {
    const seen = new Map();
    (categoryData || []).forEach((record) => {
      const key = `${record.category_id}-${record.sub_category_id}`;
      if (!seen.has(key)) {
        seen.set(key, {
          key,
          category_id: record.category_id,
          sub_category_id: record.sub_category_id,
          label: `${record.category_label || ""} - ${record.sub_category_label || ""}`.replace(/^\s*-\s*|\s*-\s*$/g, "").trim() || "Unknown",
        });
      }
    });
    return Array.from(seen.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [categoryData]);

  const visibleRows = useMemo(
    () => (selectedRowKeys.length ? categoryRows.filter((r) => selectedRowKeys.includes(r.key)) : categoryRows),
    [categoryRows, selectedRowKeys]
  );

  const dataByCategoryMonth = useMemo(() => {
    const map = {};
    (categoryData || []).forEach((record) => {
      const rowKey = `${record.category_id}-${record.sub_category_id}`;
      const monthKey = record.yearMonth;
      if (!map[rowKey]) map[rowKey] = {};
      if (!map[rowKey][monthKey]) map[rowKey][monthKey] = { ads: 0, spent: 0 };
      map[rowKey][monthKey].ads += Number(record.totalAds || 0);
      map[rowKey][monthKey].spent += Number(record.totalSpent || 0);
    });
    return map;
  }, [categoryData]);

  const grandTotalsByMonth = useMemo(() => {
    const totals = {};
    monthColumns.forEach((monthKey) => {
      totals[monthKey] = { ads: 0, spent: 0 };
      visibleRows.forEach((row) => {
        const cell = dataByCategoryMonth[row.key]?.[monthKey];
        totals[monthKey].ads += cell?.ads || 0;
        totals[monthKey].spent += cell?.spent || 0;
      });
    });
    return totals;
  }, [monthColumns, visibleRows, dataByCategoryMonth]);

  const colspan = 1 + monthColumns.length * 2;

  const handleDownloadCSV = () => {
    if (!categoryRows.length || !monthColumns.length) return;
    const csvRows = categoryRows.map((row) => {
      const obj = { Category: row.label };
      monthColumns.forEach((monthKey) => {
        const cell = dataByCategoryMonth[row.key]?.[monthKey] || { ads: 0, spent: 0 };
        obj[`${formatMonthHeader(monthKey)} - Count of Ads`] = cell.ads ? cell.ads : "";
        obj[`${formatMonthHeader(monthKey)} - Revenue (Excl GST)`] = cell.spent ?? "";
      });
      return obj;
    });
    // Totals computed across the full categoryRows (not visibleRows) so the export
    // always reflects every category, independent of the on-screen filter.
    const csvTotalsByMonth = {};
    monthColumns.forEach((monthKey) => {
      csvTotalsByMonth[monthKey] = { ads: 0, spent: 0 };
      categoryRows.forEach((row) => {
        const cell = dataByCategoryMonth[row.key]?.[monthKey];
        csvTotalsByMonth[monthKey].ads += cell?.ads || 0;
        csvTotalsByMonth[monthKey].spent += cell?.spent || 0;
      });
    });
    const totalRow = {
      Category: "Grand Total",
      ...Object.fromEntries(
        monthColumns.flatMap((m) => {
          const t = csvTotalsByMonth[m] || { ads: 0, spent: 0 };
          return [
            [`${formatMonthHeader(m)} - Count of Ads`, t.ads ?? ""],
            [`${formatMonthHeader(m)} - Revenue (Excl GST)`, t.spent ?? ""],
          ];
        })
      ),
    };
    setCsvData([...csvRows, totalRow]);
  };

  useEffect(() => {
    if (csvData.length > 0) {
      csvLinkRef.current?.link?.click();
      setCsvData([]);
    }
  }, [csvData]);

  useEffect(() => {
    if (initiateDownloadCSV) {
      console.log("initiateDownloadCSV");
      handleDownloadCSV();
      setInitiateDownloadCSV(false);
    }
  }, [initiateDownloadCSV]);

  const csvFilename = `category_wise_report_${dateRange?.[0] ? new Date(dateRange[0]).toISOString().slice(0, 10) : "start"}_${dateRange?.[1] ? new Date(dateRange[1]).toISOString().slice(0, 10) : "end"}.csv`;

  return (
    <div className="alpha-report alpha-report-category-wise">
      <CSVLink data={csvData} filename={csvFilename} className="d-none" ref={csvLinkRef} />
      <MultiSelectFilter
        label="Categories"
        options={categoryRows.map((r) => ({ value: r.key, label: r.label }))}
        selected={selectedRowKeys}
        onChange={setSelectedRowKeys}
      />
      <div className="table-responsive alpha-report-table-wrap">
        <table className="table table-bordered table-sm align-middle alpha-report-table">
          <colgroup>
            <col style={{ width: "var(--sticky-width)" }} />
            {monthColumns.flatMap((m) => [
              <col key={`${m}-ads`} style={{ width: 70 }} />,
              <col key={`${m}-spent`} style={{ width: 70 }} />,
            ])}
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={2} className="alpha-report-th-sticky-first">Category</th>
              {monthColumns.map((monthKey) => (
                <th key={monthKey} colSpan={2} className="text-center alpha-report-th-month" style={{ minWidth: 140 }}>
                  {formatMonthHeader(monthKey)}
                </th>
              ))}
            </tr>
            <tr>
              {monthColumns.map((monthKey) => (
                <React.Fragment key={`sub-${monthKey}`}>
                  <th className="alpha-report-th-sub" style={{ minWidth: 70 }}>Count of <br /> Ads</th>
                  <th className="alpha-report-th-sub" style={{ minWidth: 70 }}>Revenue <br /> (Excl GST)</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          {loader ? (
            <TableLoader colspan={colspan} />
          ) : !visibleRows.length ? (
            <NoDataFound colspan={colspan} />
          ) : (
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.key}>
                  <td
                    title={row.label}
                    className="alpha-report-td-sticky-first alpha-report-td-sticky-first-truncate"
                  >
                    {row.label}
                  </td>
                  {monthColumns.map((monthKey) => {
                    const cell = dataByCategoryMonth[row.key]?.[monthKey] || {
                      ads: 0,
                      spent: 0,
                    };
                    return (
                      <React.Fragment key={`${row.key}-${monthKey}`}>
                        <td className="text-center" style={{ minWidth: 70 }}>
                          {cell.ads || ""}
                        </td>
                        <td style={{ minWidth: 70 }} title={cell.spent}>
                          {cell.spent ? formatSpentValue(cell.spent) : ""}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
              <tr className="fw-bold">
                <td className="alpha-report-td-grand-total-sticky">Grand Total</td>
                {monthColumns.map((monthKey) => {
                  const t = grandTotalsByMonth[monthKey] || {
                    ads: 0,
                    spent: 0,
                  };
                  return (
                    <React.Fragment key={`total-${monthKey}`}>
                      <td className="text-center alpha-report-td-grand-total">{t.ads}</td>
                      <td title={t.spent} className="alpha-report-td-grand-total">
                        {t.spent ? formatSpentValue(t.spent) : ""}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
}

export default CategoryWiseReport;
